var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __param = (this && this.__param) || function (paramIndex, decorator) {
    return function (target, key) { decorator(target, key, paramIndex); }
};
import { hexToBinary, StreamPartIDUtils, waitForEvent } from '@streamr/utils';
import { randomBytes } from '@noble/post-quantum/utils';
import { Lifecycle, inject, scoped } from 'tsyringe';
import { Identity, IdentityInjectionToken } from '../identity/Identity';
import { ConfigInjectionToken } from '../ConfigTypes';
import { DestroySignal } from '../DestroySignal';
import { StreamrClientEventEmitter } from '../events';
import { uuid } from '../utils/uuid';
import { GroupKey } from './GroupKey';
import { LocalGroupKeyStore } from './LocalGroupKeyStore';
import { SubscriberKeyExchange } from './SubscriberKeyExchange';
import { StreamIDBuilder } from '../StreamIDBuilder';
import { createLazyMap } from '../utils/Mapping';
import { StreamrClientError } from '../StreamrClientError';
/**
 * Gets an explicit encryption key from config for a given stream.
 * Returns undefined if no key is configured for the stream.
 */
export const getExplicitKey = async (streamId, streamIdBuilder, config) => {
    if (config.keys !== undefined) {
        for (const entry of Object.entries(config.keys)) {
            if (await streamIdBuilder.toStreamID(entry[0]) === streamId) {
                return new GroupKey(entry[1].id, Buffer.from(hexToBinary(entry[1].data)));
            }
        }
    }
    return undefined;
};
let GroupKeyManager = class GroupKeyManager {
    subscriberKeyExchange;
    localGroupKeyStore;
    explicitKeys;
    config;
    identity;
    eventEmitter;
    destroySignal;
    constructor(subscriberKeyExchange, localGroupKeyStore, streamIdBuilder, config, identity, eventEmitter, destroySignal) {
        this.subscriberKeyExchange = subscriberKeyExchange;
        this.localGroupKeyStore = localGroupKeyStore;
        this.config = config;
        this.identity = identity;
        this.eventEmitter = eventEmitter;
        this.destroySignal = destroySignal;
        if (config.encryption.keys !== undefined) {
            this.explicitKeys = createLazyMap({
                valueFactory: async (streamId) => {
                    return getExplicitKey(streamId, streamIdBuilder, config.encryption);
                }
            });
        }
    }
    async fetchKey(streamPartId, groupKeyId, publisherId) {
        // If explicit keys are defined only those keys are used.
        if (this.explicitKeys !== undefined) {
            const explicitKey = await this.explicitKeys.get(StreamPartIDUtils.getStreamID(streamPartId));
            if (explicitKey !== undefined) {
                return explicitKey;
            }
            throw new StreamrClientError(`No encryption key available for stream part ID: groupKeyId=${groupKeyId}, streamPartId=${streamPartId}`, 'UNEXPECTED_INPUT');
        }
        // 2nd try: local storage
        let groupKey = await this.localGroupKeyStore.get(groupKeyId, publisherId);
        if (groupKey !== undefined) {
            return groupKey;
        }
        // 3rd try: Streamr key-exchange
        await this.subscriberKeyExchange.requestGroupKey(groupKeyId, publisherId, streamPartId);
        const groupKeyIds = await waitForEvent(
        // TODO remove "as any" type casing in NET-889
        this.eventEmitter, 'encryptionKeyStoredToLocalStore', this.config.encryption.keyRequestTimeout, (storedGroupKeyId) => storedGroupKeyId === groupKeyId, this.destroySignal.abortSignal);
        groupKey = await this.localGroupKeyStore.get(groupKeyIds[0], publisherId);
        return groupKey;
    }
    async fetchLatestEncryptionKey(publisherId, streamId) {
        if (publisherId !== (await this.identity.getUserId())) {
            throw new Error('storeKey: fetching latest encryption keys for other publishers not supported.');
        }
        const keyId = await this.localGroupKeyStore.getLatestEncryptionKeyId(publisherId, streamId);
        return keyId !== undefined ? this.localGroupKeyStore.get(keyId, publisherId) : undefined;
    }
    async storeKey(groupKey, publisherId, streamId) {
        if (publisherId !== (await this.identity.getUserId())) { // TODO: unit test?
            throw new Error('storeKey: storing latest encryption keys for other publishers not supported.');
        }
        if (groupKey === undefined) {
            const keyData = Buffer.from(randomBytes(32));
            groupKey = new GroupKey(uuid('GroupKey'), keyData);
        }
        await this.localGroupKeyStore.set(groupKey.id, publisherId, groupKey.data);
        await this.localGroupKeyStore.setLatestEncryptionKeyId(groupKey.id, publisherId, streamId);
        return groupKey;
    }
    addKeyToLocalStore(groupKey, publisherId) {
        return this.localGroupKeyStore.set(groupKey.id, publisherId, groupKey.data);
    }
};
GroupKeyManager = __decorate([
    scoped(Lifecycle.ContainerScoped),
    __param(3, inject(ConfigInjectionToken)),
    __param(4, inject(IdentityInjectionToken)),
    __metadata("design:paramtypes", [SubscriberKeyExchange,
        LocalGroupKeyStore,
        StreamIDBuilder, Object, Identity,
        StreamrClientEventEmitter,
        DestroySignal])
], GroupKeyManager);
export { GroupKeyManager };
